import Chip from '@mui/material/Chip';
import SubdirectoryArrowRightIcon from '@mui/icons-material/SubdirectoryArrowRight';
import InternalBookingCardBase from '../common/InternalBookingCardBase';
import { generateUniqueColor } from '../../../../util/colorUtil';
import { extractUpgradeSymbols } from '../../../../util/upgradeSymbolsHelper';
import { isInactiveBookingStatus } from '../../../../util/dateUtils';

const normalizeToIntervalStart = (date, intervalMs) => {
  if (!(date instanceof Date) || !Number.isFinite(intervalMs) || intervalMs <= 0) {
    return date;
  }
  const normalized = Math.floor(date.getTime() / intervalMs) * intervalMs;
  return new Date(normalized);
};

const getResourceDisplayName = (resourceId, orderedResourcesConfig) => {
  const rid = resourceId ? String(resourceId) : '';
  if (!rid) return '';
  const nameById = orderedResourcesConfig?.nameById;
  if (nameById instanceof Map) {
    return nameById.get(rid) || rid.substring(0, 6);
  }
  return rid.substring(0, 6);
};

// Computes available shift times adjusted by the segment's prefix duration offset
function computeAvailableShiftTimesWithPrefix(times, getPrefixDurationMs, index) {
  try {
    if (!Array.isArray(times) || times.length === 0) {
      return times;
    }
    const offsetMin = Math.round(getPrefixDurationMs(index) / 60000);
    const totalDayMin = 24 * 60;
    return times.map(t => {
      const [hh, mm] = String(t).split(':').map(Number);
      if (Number.isNaN(hh) || Number.isNaN(mm)) return t;
      let mins = hh * 60 + mm + offsetMin;
      mins = ((mins % totalDayMin) + totalDayMin) % totalDayMin;
      const nh = Math.floor(mins / 60);
      const nm = mins % 60;
      return `${String(nh).padStart(2, '0')}:${String(nm).padStart(2, '0')}`;
    });
  } catch {
    return times;
  }
}

/**
 * Renders an internal booking as multiple sequential segments across ordered resources.
 * Each segment is positioned on the lane of its resource and spans its configured duration.
 */
function BookingCardOrderedResources({
  booking,
  orderedResourcesConfig, // { order: resourceIds[], laneIndexById: Map, durationByIdMs: Map }
  laneOffsetDisplayIndexById = new Map(),
  segmentStackIndexByKey = null,
  baseSlotCountById = null,
  handleOpenModal,
  onBookingShift,
  entertainment,
  availableShiftTimes,
  isShiftable,
  isBookingReadyForShift,
  onDragStart,
  onDragActivated,
  onDragEnd,
  // optional live preview feed from base card drag
  onDragPreview,
  // when set, segments should be rendered starting at this time (preview)
  previewStartTime,
  onHoverStart,
  onHoverEnd,
  externallyHighlightedBookingId,
  rowHeight,
  slotWidthPx = 140,
  minutesPerSlot = 30,
  // external drag state to elevate all segments of the booking
  externallyDraggedBookingId = null,
}) {
  if (
    !booking ||
    !Array.isArray(booking.orderedResourceIds) ||
    booking.orderedResourceIds.length === 0
  ) {
    return null;
  }

  // Defensive: a cancelled/rejected leftover that still carries resource segments but has zero
  // participants is a stale "ghost" record. It must never occupy a lane, otherwise it renders a
  // duplicate greyed "0" card next to the still-active booking. The backend already filters these
  // out; this guard also covers a stale cached day list until that response refreshes.
  if (isInactiveBookingStatus(booking.status) && Number(booking.participantCount) <= 0) {
    return null;
  }

  const bookingIdStr = booking?._id?.toString?.() || String(booking._id);

  const upgradeSymbols = extractUpgradeSymbols(booking);

  const isIntervalMode = Boolean(orderedResourcesConfig?.isIntervalMode);
  const intervalMs = orderedResourcesConfig?.intervalMs || null;
  const laneCountById = orderedResourcesConfig?.laneCountById || new Map();

  const groupParticipantsByIndex = booking.orderedResourceIds.reduce((acc, entry) => {
    const groupIdx =
      entry && Number.isFinite(entry.groupIndex) && entry.groupIndex > 0 ? entry.groupIndex : 1;
    const p = Number(entry?.participantCount || 0);
    if (!acc.has(groupIdx) && Number.isFinite(p) && p > 0) {
      acc.set(groupIdx, p);
    }
    return acc;
  }, new Map());

  const planEntries = booking.orderedResourceIds
    .map((entry, idx) => {
      const ridRaw = entry?.resourceId?._id || entry?.resourceId;
      const rid = ridRaw ? String(ridRaw) : null;
      if (!rid) return null;
      return {
        resourceId: rid,
        startOffsetMinutes:
          typeof entry?.startOffsetMinutes === 'number' ? entry.startOffsetMinutes : null,
        durationMinutes: typeof entry?.durationMinutes === 'number' ? entry.durationMinutes : null,
        groupIndex:
          entry && Number.isFinite(entry.groupIndex) && entry.groupIndex > 0 ? entry.groupIndex : 1,
        orderPosition:
          entry && Number.isFinite(entry.orderPosition) && entry.orderPosition > 0
            ? entry.orderPosition
            : idx + 1,
      };
    })
    .filter(Boolean)
    .sort((a, b) => {
      const aOffset = Number.isFinite(a.startOffsetMinutes)
        ? a.startOffsetMinutes
        : Number.POSITIVE_INFINITY;
      const bOffset = Number.isFinite(b.startOffsetMinutes)
        ? b.startOffsetMinutes
        : Number.POSITIVE_INFINITY;
      if (aOffset !== bOffset) {
        return aOffset - bOffset;
      }
      return a.orderPosition - b.orderPosition;
    });

  if (planEntries.length === 0) {
    return null;
  }

  const metadataAvailable = planEntries.every(
    entry => Number.isFinite(entry.startOffsetMinutes) && Number.isFinite(entry.durationMinutes)
  );

  const baseStartMs = previewStartTime
    ? new Date(previewStartTime).getTime()
    : new Date(booking.startTime).getTime();

  const configuredDurationsMs = planEntries.map(
    entry => orderedResourcesConfig.durationByIdMs.get(entry.resourceId) || 0
  );
  const hasAllConfiguredDurations = configuredDurationsMs.every(v => v > 0);

  let segmentsData;
  if (metadataAvailable) {
    segmentsData = planEntries.map((entry, idx) => {
      const offsetMs = entry.startOffsetMinutes * 60000;
      const fallbackDuration = configuredDurationsMs[idx] || 0;
      const durationMsCandidate = entry.durationMinutes * 60000;
      const durationMs = durationMsCandidate > 0 ? durationMsCandidate : fallbackDuration;
      const participantsForGroup =
        groupParticipantsByIndex.get(entry.groupIndex) || booking.participantCount;
      return {
        resourceId: entry.resourceId,
        groupIndex: entry.groupIndex,
        orderPosition: entry.orderPosition,
        startMs: baseStartMs + offsetMs,
        durationMs,
        participantCount: participantsForGroup,
      };
    });
  } else {
    let durationsMs;
    if (hasAllConfiguredDurations) {
      durationsMs = configuredDurationsMs;
    } else {
      const totalDurationMs = booking.eventDurationMS || 0;
      const per = Math.floor(totalDurationMs / planEntries.length);
      durationsMs = planEntries.map((_, idx) =>
        idx === planEntries.length - 1 ? Math.max(0, totalDurationMs - per * idx) : per
      );
    }
    let accOffset = 0;
    segmentsData = planEntries.map((entry, idx) => {
      const durationMs = durationsMs[idx] || 0;
      const startMs = baseStartMs + accOffset;
      accOffset += durationMs;
      const participantsForGroup =
        groupParticipantsByIndex.get(entry.groupIndex) || booking.participantCount;
      return {
        resourceId: entry.resourceId,
        groupIndex: entry.groupIndex,
        orderPosition: entry.orderPosition,
        startMs,
        durationMs,
        participantCount: participantsForGroup,
      };
    });
  }

  segmentsData.sort((a, b) => {
    if (a.startMs !== b.startMs) {
      return a.startMs - b.startMs;
    }
    if ((a.groupIndex || 1) !== (b.groupIndex || 1)) {
      return (a.groupIndex || 1) - (b.groupIndex || 1);
    }
    if ((a.orderPosition || 0) !== (b.orderPosition || 0)) {
      return (a.orderPosition || 0) - (b.orderPosition || 0);
    }
    return a.resourceId.localeCompare(b.resourceId);
  });

  const segmentsByGroup = segmentsData.reduce((acc, seg) => {
    const groupIndex = Number.isFinite(Number(seg.groupIndex)) && Number(seg.groupIndex) > 0
      ? Number(seg.groupIndex)
      : 1;
    if (!acc.has(groupIndex)) {
      acc.set(groupIndex, []);
    }
    acc.get(groupIndex).push(seg);
    return acc;
  }, new Map());

  segmentsByGroup.forEach(groupSegments => {
    const orderedGroupSegments = groupSegments.slice().sort((a, b) => {
      if (a.startMs !== b.startMs) {
        return a.startMs - b.startMs;
      }
      return (a.orderPosition || 0) - (b.orderPosition || 0);
    });

    let previousDistinctSegment = null;
    orderedGroupSegments.forEach(seg => {
      if (!previousDistinctSegment) {
        previousDistinctSegment = seg;
        return;
      }

      if (seg.resourceId === previousDistinctSegment.resourceId) {
        return;
      }

      seg.resourceSwitch = {
        fromResourceName: getResourceDisplayName(previousDistinctSegment.resourceId, orderedResourcesConfig),
        toResourceName: getResourceDisplayName(seg.resourceId, orderedResourcesConfig),
      };
      previousDistinctSegment = seg;
    });
  });

  const totalGroupCount = segmentsData.reduce((max, seg) => {
    const groupValue = Number.isFinite(seg.groupIndex) ? seg.groupIndex : 1;
    return Math.max(max, groupValue);
  }, 1);

  const getPrefixDurationMs = idx => {
    const seg = segmentsData[idx];
    if (!seg) return 0;
    return seg.startMs - baseStartMs;
  };

  const segments = segmentsData.map((segData, idx) => {
    const rid = segData.resourceId;
    const baseLaneIndex = orderedResourcesConfig.laneIndexById.get(rid) || 1;
    const laneCount = laneCountById.get(rid) || 1;
    const baseSlotCount =
      baseSlotCountById instanceof Map ? Math.max(1, Number(baseSlotCountById.get(rid) || 1)) : 1;
    const durationMs = segData.durationMs || 0;
    const segStartDate = new Date(segData.startMs);
    const segStartISO = segStartDate.toISOString();
    const groupIndex = segData.groupIndex || 1;

    let laneOffset = 0;
    if (isIntervalMode && laneCount > 1 && Number.isFinite(intervalMs) && intervalMs > 0) {
      const slotStart = normalizeToIntervalStart(segStartDate, intervalMs);
      const slotISO = slotStart.toISOString();
      const slotIndex = Math.floor(slotStart.getTime() / intervalMs);
      const timeOffset = ((slotIndex % baseSlotCount) + baseSlotCount) % baseSlotCount;
      const stackKey = `${bookingIdStr}:${rid}:${slotISO}`;
      const stackIndex =
        segmentStackIndexByKey instanceof Map
          ? Number(segmentStackIndexByKey.get(stackKey) || 0)
          : 0;
      const safeStack = Number.isFinite(stackIndex) && stackIndex >= 0 ? Math.floor(stackIndex) : 0;
      laneOffset = timeOffset + safeStack * baseSlotCount;
    } else if (isIntervalMode && laneCount > 1) {
      laneOffset = (((groupIndex - 1) % laneCount) + laneCount) % laneCount;
    }

    const offsetMapping = laneOffsetDisplayIndexById?.get(rid) || null;
    let laneIndex = offsetMapping?.get(laneOffset);
    if (!laneIndex && offsetMapping?.size) {
      laneIndex = offsetMapping.values().next().value;
    }
    if (!laneIndex) {
      laneIndex = baseLaneIndex + laneOffset;
    }
    return {
      laneIndex,
      resourceId: rid,
      startTime: segStartISO,
      durationMs,
      groupIndex: segData.groupIndex,
      groupCount: totalGroupCount,
      isFirst: idx === 0,
      resourceSwitch: segData.resourceSwitch || null,
      participantCount: segData.participantCount,
    };
  });

  const handleCardClick = bookingId => {
    handleOpenModal(bookingId);
  };

  // Emit preview based on overall booking start, regardless of which segment is dragged
  const makePreviewHandlerForIndex = idx => (bk, newStart) => {
    try {
      if (!newStart) {
        onDragPreview?.(null, null);
        return;
      }
      const baseStart = new Date(newStart.getTime() - getPrefixDurationMs(idx));
      onDragPreview?.(booking, baseStart);
    } catch {
      onDragPreview?.(bk, newStart);
    }
  };

  return segments.map((seg, i) => {
    // Border color should be based on booking id (consistent across segments)
    const color = generateUniqueColor(String(booking._id));
    const isExternallyHighlighted = externallyHighlightedBookingId === String(booking._id);
    const fakeBooking = {
      ...booking,
      startTime: seg.startTime,
      eventDurationMS: seg.durationMs,
      groupIndex: seg.groupIndex,
      groupCount: seg.groupCount,
      i: seg.laneIndex,
      participantCount: seg.participantCount,
      groupParticipantCount: seg.participantCount,
    };

    const segmentIsShiftable = bk => Boolean(isShiftable?.(bk));
    const handleSegmentShift = (bk, newStartIso) => {
      try {
        const d = new Date(newStartIso);
        const adjusted = new Date(d.getTime() - getPrefixDurationMs(i));
        return onBookingShift?.(booking, adjusted.toISOString());
      } catch {
        return onBookingShift?.(booking, newStartIso);
      }
    };
    const segmentReadyForShift = bk => Boolean(isBookingReadyForShift?.(bk));
    const switchInfo = seg.resourceSwitch;
    const switchLabel = switchInfo?.toResourceName
      ? `Wechsel zu ${switchInfo.toResourceName}`
      : 'Ressourcenwechsel';

    return (
      <InternalBookingCardBase
        key={`${booking._id}-seg-${i}`}
        booking={fakeBooking}
        getParticipantCount={bk => bk.participantCount}
        onCardClick={handleCardClick}
        onHoverStart={onHoverStart}
        onHoverEnd={onHoverEnd}
        renderAvailableSlots={false}
        rowHeight={rowHeight}
        slotWidthPx={slotWidthPx}
        minutesPerSlot={minutesPerSlot}
        showIcons={false}
        showPricingName={false}
        showDuration={false}
        showStartTimeInFirstLine={false}
        borderStyle={switchInfo ? 'dashed' : 'solid'}
        renderAfterName={
          switchInfo
            ? () => (
                <Chip
                  size='small'
                  variant='outlined'
                  icon={<SubdirectoryArrowRightIcon sx={{ fontSize: 14 }} />}
                  label={switchLabel}
                  title={
                    switchInfo.fromResourceName && switchInfo.toResourceName
                      ? `Ressourcenwechsel von ${switchInfo.fromResourceName} zu ${switchInfo.toResourceName}`
                      : 'Ressourcenwechsel'
                  }
                  sx={{
                    ml: 0.75,
                    height: 18,
                    maxWidth: 180,
                    fontSize: '0.65rem',
                    fontWeight: 700,
                    verticalAlign: 'text-bottom',
                    borderColor: color,
                    '& .MuiChip-label': {
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: 'block',
                    },
                  }}
                />
              )
            : undefined
        }
        entertainment={entertainment}
        onDragStart={(bk, showTimes) => onDragStart?.(booking, showTimes)}
        onDragActivated={onDragActivated}
        onDragEnd={onDragEnd}
        onDragPreview={makePreviewHandlerForIndex(i)}
        isExternallyDragged={externallyDraggedBookingId === String(booking._id)}
        disableHoverEffects={
          Boolean(externallyDraggedBookingId) &&
          String(externallyDraggedBookingId) !== String(booking._id)
        }
        availableShiftTimes={computeAvailableShiftTimesWithPrefix(
          availableShiftTimes,
          getPrefixDurationMs,
          i
        )}
        isShiftable={segmentIsShiftable}
        onBookingShift={handleSegmentShift}
        isBookingReadyForShift={segmentReadyForShift}
        isExternallyHighlighted={isExternallyHighlighted}
        iconProps={{
          upgrades: upgradeSymbols,
          manualUpgrades: [],
          participantCount: booking.participantCount,
          color,
        }}
        color={color}
      />
    );
  });
}

export default BookingCardOrderedResources;
